import { generateId } from '@freelancer/datastore/testing/helpers';
import { TimeUnitApi } from 'api-typings/common/common';
import type {
  DeloitteIndustryApi,
  DeloitteOfferingApi,
  DeloittePracticeApi,
  DeloitteProjectTypeApi,
} from 'api-typings/projects/deloitte';
import { OnBehalfProjectStatusApi } from 'api-typings/projects/projects';
import { addDays } from 'date-fns';
import type { OnBehalfProject } from './on-behalf-projects.model';
import type { CustomFieldValue } from '../custom-field-info-configurations/custom-field-info-configurations.model';
import { Pool } from '../enterprise/enterprise.model';
import type { ProjectLocalDetails } from '../project-view-projects/project-view-projects.model';
import type { Location } from '../project-view-users/location.model';
import { phpSkill } from '../skills/skills.seed';

export interface GenerateOnBehalfProjectOptions {
  readonly userId: number;
  readonly projectId?: number; // only for projects that have been posted live
  readonly nominatedUserEmail: string;
  readonly nominatedUserId?: number;
  readonly id?: number;
  readonly title?: string;
  readonly status?: OnBehalfProjectStatusApi;
  readonly poolIds?: readonly number[];
  readonly currencyId?: number;
  readonly hourlyProjectInfo?: {
    readonly commitment: {
      readonly hours: number;
      readonly interval: TimeUnitApi;
    };
  };
  // TODO: T213935 - This is deprecated and will be removed.
  readonly deloitteDetails?: {
    readonly billingCode: string;
    readonly industryOffering: {
      readonly projectType: DeloitteProjectTypeApi;
      readonly practice: DeloittePracticeApi;
      readonly industry: DeloitteIndustryApi;
      readonly offering: DeloitteOfferingApi;
    };
    readonly clearance?: string;
    readonly itar?: boolean;
  };
  readonly customFieldValues?: readonly CustomFieldValue[];
  readonly localDetails?: ProjectLocalDetails;
  readonly location?: Location;
  readonly isExpired?: boolean;
}

export function generateOnBehalfProject({
  userId,
  projectId,
  nominatedUserEmail,
  nominatedUserId,
  id = generateId(),
  title = 'On Behalf Project',
  status = OnBehalfProjectStatusApi.PENDING,
  currencyId = 1,
  poolIds = [],
  hourlyProjectInfo,
  deloitteDetails,
  customFieldValues,
  localDetails,
  location,
  isExpired,
}: GenerateOnBehalfProjectOptions): OnBehalfProject {
  return {
    id,
    userId,
    nominatedUserEmail,
    nominatedUserId,
    title,
    description: 'This is the default description for the On behalf project.',
    currencyId,
    budget: {
      minimum: 1,
      maximum: 1,
    },
    skills: [phpSkill()],
    budgetType: 'hourly',
    upgrades: {
      assisted: false,
      NDA: false,
      nonpublic: false,
      sealed: false,
      featured: false,
      urgent: false,
      fulltime: false,
      ipContract: false,
      listed: false,
      projectManagement: false,
      qualified: false,
      extend: false,
    },
    fileIds: [],
    timeframe: {
      startDate: Date.now(),
      endDate: addDays(Date.now(), 3).getTime(),
    },
    poolIds,
    hourlyProjectInfo,
    status,
    deloitteDetails,
    projectId:
      status === OnBehalfProjectStatusApi.POSTED
        ? (projectId ?? generateId())
        : undefined,
    customFieldValues,
    localDetails,
    location,
    isExpired,
  };
}

export function deloitteOnBehalfProject(
  customFieldValues: readonly CustomFieldValue[] = [],
): Pick<
  GenerateOnBehalfProjectOptions,
  | 'poolIds'
  | 'currencyId'
  | 'hourlyProjectInfo'
  | 'deloitteDetails'
  | 'customFieldValues'
> {
  return {
    poolIds: [Pool.DELOITTE_DC],
    currencyId: 40, // TKN
    hourlyProjectInfo: {
      commitment: { hours: 123, interval: TimeUnitApi.WEEK },
    },
    customFieldValues,
  };
}

export function freightlancerOnBehalfProject(): Pick<
  GenerateOnBehalfProjectOptions,
  'poolIds' | 'currencyId'
> {
  return {
    poolIds: [Pool.FREIGHTLANCER],
    currencyId: 3, // AUD
  };
}
